import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { authService } from '../../services/authService'
import { ArrowLeft, Mail, KeyRound } from 'lucide-react'

interface ForgotPasswordFormData {
    email: string
}

export const ForgotPasswordPage: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<ForgotPasswordFormData>({
        mode: 'onChange',
    })

    const onSubmit = async (data: ForgotPasswordFormData) => {
        try {
            setIsLoading(true)
            const response = await authService.forgotPassword(data.email)
            if (response.success) {
                setIsSubmitted(true)
                toast.success(response.message || 'Reset link sent to your email')
            } else {
                toast.error(response.message || 'Failed to send reset link')
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Something went wrong')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-app-bg py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background Ambient Glows */}
            <div className="absolute top-[-200px] right-[-100px] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] pointer-events-none"></div>
            <div className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] bg-info/10 rounded-full blur-[150px] pointer-events-none"></div>
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

            <div className="max-w-md w-full space-y-8 z-10">
                <div className="text-center">
                    <div className="flex justify-center mb-0">
                        <Link to="/" className="flex items-center gap-3 group">
                            <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-20 w-auto object-contain drop-shadow-[0_0_20px_rgba(255,107,0,0.4)] group-hover:scale-105 transition-all duration-500" />
                        </Link>
                    </div>
                    <h2 className="text-4xl font-black text-white mt-4 tracking-tight">{isSubmitted ? 'Link Sent' : 'Reset Password'}</h2>
                    <p className="mt-4 text-sm text-text-secondary font-medium tracking-wide">
                        {isSubmitted
                            ? "Check your email for a password reset link."
                            : "Enter your email to reset your password."}
                    </p>
                </div>

                <Card className="bg-black/40 backdrop-blur-xl border-white/5 shadow-2xl overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-orange-500 to-amber-500 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xl font-bold text-white flex items-center gap-3">
                            {isSubmitted ? <Mail size={20} className="text-primary" /> : <KeyRound size={20} className="text-primary" />} 
                            {isSubmitted ? 'Email Sent' : 'Forgot Password'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!isSubmitted ? (
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 mt-4">
                                <Input
                                    label="Email Address"
                                    type="email"
                                    placeholder="Enter your email"
                                    icon={Mail}
                                    error={errors.email?.message}
                                    {...register('email', {
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address',
                                        },
                                    })}
                                />

                                <Button type="submit" className="w-full py-6 bg-primary hover:bg-orange-600 text-white font-bold uppercase tracking-widest shadow-[0_10px_30px_rgba(255,107,0,0.3)] hover:shadow-primary/50 transition-all duration-300" disabled={!isValid || isLoading} loading={isLoading}>
                                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                                </Button>

                                <div className="text-center mt-4">
                                    <Link to="/login" className="inline-flex items-center text-xs font-bold tracking-wide text-text-secondary hover:text-primary transition-all group">
                                        <ArrowLeft className="w-4 h-4 mr-3 group-hover:-translate-x-1 transition-transform" />
                                        Back to Login
                                    </Link>
                                </div>
                            </form>
                        ) : (
                            <div className="text-center space-y-8 py-6">
                                <div className="flex justify-center">
                                    <div className="w-20 h-20 bg-primary/10 border border-primary/30 rounded-[2rem] flex items-center justify-center shadow-[0_0_30px_rgba(255,107,0,0.25)] animate-pulse">
                                        <Mail className="w-10 h-10 text-primary" />
                                    </div>
                                </div>
                                <p className="text-text-secondary font-medium leading-relaxed tracking-wide opacity-80">
                                    We've sent a password reset link to your email. Click the link to securely reset your password.
                                </p>
                                <div className="pt-4 flex flex-col gap-4">
                                    <Link to="/login" className="w-full">
                                        <Button className="w-full py-6 bg-primary hover:bg-orange-600 text-white font-bold uppercase tracking-widest">Return to Login</Button>
                                    </Link>
                                    <button 
                                        onClick={() => setIsSubmitted(false)}
                                        className="text-xs font-bold tracking-wide text-text-secondary hover:text-white transition-colors"
                                    >
                                        Try a different email
                                    </button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
